import { useMemo, useState } from "react";

import {
  filterProjectsByStatus,
  initialStatusTab,
  orderActiveFirst,
  partitionProjectsByStatus,
  shouldSplitByStatus,
  type ProjectStatusFilterValue,
  type ProjectWithStatus,
} from "./projectStatus";
import { useProjectStatusEnabled } from "./projectStatusAvailability";

export interface ProjectStatusFilterState<T> {
  /** The filter in effect. */
  filter: ProjectStatusFilterValue;
  setFilter: (filter: ProjectStatusFilterValue) => void;
  /** The projects to render: filtered, with the selection always kept, active
   *  first. For plain lists. */
  visible: T[];
  /** Every project, active first. What a Radix `Select` maps over (with
   *  `isShown`), so its order matches the plain lists'. */
  ordered: T[];
  /**
   * Whether the filter shows a project. For a Radix `Select`, render EVERY
   * project and give the ones this rejects `hidden` + `disabled` instead of
   * dropping them: its hidden native `<select>` resets the value to "" when a
   * value arrives for an option not mounted yet — which is exactly what a
   * selection set a render after opening (a scoped project) would hit.
   */
  isShown: (project: T) => boolean;
  counts: { active: number; inactive: number; all: number };
  /** False at or below PROJECT_STATUS_SPLIT_THRESHOLD projects: the control
   *  stays hidden and every project shows, told apart by its status dot. */
  showFilter: boolean;
}

/**
 * State for the status filter beside a project choice. Until the user picks a
 * filter, it follows the selection (an inactive selected project opens on
 * Inactive); the choice is derived rather than seeded so a list that loads
 * after the first render still opens on the right filter.
 */
export function useProjectStatusFilter<T extends ProjectWithStatus>(
  projects: readonly T[],
  selectedId?: number | string | null
): ProjectStatusFilterState<T> {
  const [chosen, setChosen] = useState<ProjectStatusFilterValue | null>(null);
  const statusEnabled = useProjectStatusEnabled();

  const counts = useMemo(() => {
    const { active, inactive } = partitionProjectsByStatus(projects);
    return { active: active.length, inactive: inactive.length, all: projects.length };
  }, [projects]);

  const showFilter = shouldSplitByStatus(projects.length, statusEnabled);
  const filter: ProjectStatusFilterValue = showFilter
    ? (chosen ?? initialStatusTab(projects, selectedId))
    : "all";

  const ordered = useMemo(() => orderActiveFirst(projects), [projects]);
  const visible = useMemo(
    () => filterProjectsByStatus(ordered, filter, selectedId),
    [ordered, filter, selectedId]
  );

  const isShown = useMemo(() => {
    const shown = new Set(visible.map((project) => String(project.id)));
    return (project: T) => shown.has(String(project.id));
  }, [visible]);

  return { filter, setFilter: setChosen, visible, ordered, isShown, counts, showFilter };
}
